import React, { useEffect, useRef, useState } from 'react'

import styled from '@emotion/styled'

import { spacing } from '../../theme'
import { Flex } from '../layout/Flex'
import ArcadeRadioButton from '../RadioButton/ArcadeRadioButton'
import Text from '../Text/Text'

const SPACING_OFFSET = 2

/** Segemented Control Slider */
const SegmentedControlSlider = styled.div<{
  position: 'left' | 'right'
}>`
  position: absolute;
  top: ${({ theme }) => theme.pxToRem(SPACING_OFFSET)};
  bottom: ${({ theme }) => theme.pxToRem(SPACING_OFFSET)};
  left: ${({ position, theme }) => (position === 'left' ? theme.pxToRem(SPACING_OFFSET) : '50%')};
  width: calc(50% - ${({ theme }) => theme.pxToRem(SPACING_OFFSET)});
  background-color: ${({ theme }) => theme.arcadeColors.component.segmentedControlButtonBackgroundSelected};
  border-radius: ${({ theme }) => theme.border.radius.pill};
  transition: left 200ms ease;
`

/** Segmented Control Option */
export interface SegmentedControlOption {
  label: string
  value: string
}

const OptionLabel = styled.label`
  cursor: pointer;
  height: 100%;
  flex: 1;
  max-width: 50%;
  padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.s};

  ${({ theme }) => theme.selectors.keyboard} &:has(input:focus) {
    outline: ${({ theme }) => theme.pxToRem(2)} solid ${({ theme }) => theme.arcadeColors.semantic.borderProminent};
    border-radius: ${({ theme }) => theme.border.radius.pill};
  }
`

const OptionContainer = styled(Flex)`
  height: 100%;
  align-items: center;
`

const OptionText = styled(Text)`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;
  width: 100%;
  text-align: center;
`

interface SegmentedControlInputProps {
  label: string
  name?: string
  value: string
  checked?: boolean
  onChange?: () => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

const SegmentedControlInput = ({ label, name, value, ...restProps }: SegmentedControlInputProps) => {
  const inputId = `${name}-${value}`

  return (
    <OptionLabel>
      <OptionContainer flexDirection="row" gap={spacing.s}>
        <ArcadeRadioButton {...restProps} id={inputId} name={name} value={value} isSegmentedControl={true} />
        <OptionText color="textStandard" typography="button">
          {label}
        </OptionText>
      </OptionContainer>
    </OptionLabel>
  )
}

/** Segemented Control component */
const SegmentedControlContainer = styled.fieldset`
  display: flex;
  flex-direction: row;
  background-color: ${({ theme }) => theme.arcadeColors.component.segmentedControlBackgroundDefault};
  border-radius: ${({ theme }) => theme.border.radius.pill};
  height: 3.25rem;
  position: relative;
  padding: ${({ theme }) => theme.pxToRem(SPACING_OFFSET)};
`

const OptionsContainer = styled(Flex)`
  position: relative;
  width: 100%;
`

export type SegmentedControlProps = {
  options: [SegmentedControlOption, SegmentedControlOption]
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  ariaLabel: string
  name?: string
}

export const SegmentedControl = React.forwardRef<HTMLFieldSetElement, SegmentedControlProps>(
  ({ options, value, defaultValue, onChange, ariaLabel, name }, ref) => {
    const containerRef = useRef<HTMLFieldSetElement>(null)
    const optionRefs = useRef<(HTMLInputElement | null)[]>([])

    const isControlled = value !== undefined
    const initialValue = isControlled ? value : defaultValue || options[0]?.value || ''
    const [internalValue, setInternalValue] = useState(initialValue)
    const currentValue = isControlled ? value : internalValue

    const selectedIndex = options.findIndex(opt => opt.value === currentValue)
    const sliderPosition = selectedIndex === 1 ? 'right' : 'left'

    const handleOptionClick = (optionValue: string) => {
      if (!isControlled) {
        setInternalValue(optionValue)
      }
      onChange?.(optionValue)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, currentIndex: number) => {
      const keyMap: Record<string, number> = {
        ArrowLeft: 0,
        ArrowRight: 1,
      }

      const newIndex = keyMap[event.key]
      if (newIndex === undefined || newIndex === currentIndex) return

      event.preventDefault()
      const newValue = options[newIndex].value

      if (!isControlled) {
        setInternalValue(newValue)
      }
      onChange?.(newValue)

      // Focus the next radio input
      setTimeout(() => {
        const nextInput = optionRefs.current[newIndex]
        if (nextInput) {
          nextInput.focus()
        }
      }, 0)
    }

    // Update refs when options change
    useEffect(() => {
      if (containerRef.current) {
        const radioInputs = containerRef.current.querySelectorAll('input[type="radio"]')
        optionRefs.current = Array.from(radioInputs) as HTMLInputElement[]
      }
    }, [options])

    return (
      <SegmentedControlContainer ref={ref || containerRef} role="radiogroup" aria-label={ariaLabel}>
        <SegmentedControlSlider position={sliderPosition} />
        <OptionsContainer>
          {options.map((option, index) => {
            const isSelected = selectedIndex === index
            return (
              <SegmentedControlInput
                key={option.value}
                name={name}
                value={option.value}
                checked={isSelected}
                onChange={() => handleOptionClick(option.value)}
                onKeyDown={e => handleKeyDown(e, index)}
                label={option.label}
              />
            )
          })}
        </OptionsContainer>
      </SegmentedControlContainer>
    )
  }
)

SegmentedControl.displayName = 'SegmentedControl'

export default SegmentedControl
